import { FormField, isolate } from '@batiflow/ui';
import { Button, Select, __css, tokens } from '@fluentui/react-components';
import {
  ArrowDown16Regular,
  ArrowUp16Regular,
  Delete16Regular,
  PersonAdd20Regular,
  Building20Regular,
  Add20Regular,
} from '@fluentui/react-icons';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  MAX_OFFICES,
  MAX_REVIEWERS,
  MAX_STEPS,
  STEP_MODES,
  moveItem,
  newStep,
  type StepDraft,
} from './approvals-model';

const useStyles = __css({"root":{"mc9l5x":"f22iagw","Beiy3e4":"f1vx9l62","Belr9w4":"f1noh358"},"step":{"mc9l5x":"f22iagw","Beiy3e4":"f1vx9l62","Belr9w4":"f1wn3y0l","Bokbzmb":0,"Jyy4pa":0,"B1u1w3b":"f1r0i6kl","Dbcxam":0,"rjzwhg":0,"Bblux5w":"f9b8bpc","Bnt446c":0,"B1hrpyx":0,"Fjva8n":"f12n3xlw","Beyfa6y":0,"Bbmb7ep":0,"Btl43ni":0,"B7oj6ja":0,"Dimara":"ft85np5","gggo0z":"fi15kbk","Behc1zw":"f10zy6vx","B4m11q7":"f32hy0g","tzxy30":"fgmlvap","B834pou":"f1taw3pw","uk9mye":"f1p2dij1","rm1204":"fwyddsq","Bwezv66":"fwl1lcr","F1poyb":"foi3q75","mbw9mh":"f1a566p","ijz38e":"f1ftuoky","Ctt3hk":"f1crbv9w","Bgto7am":"fuzgk9b"},"legend":{"Bhrd7zp":"fl43uef","Bnt446c":0,"B1hrpyx":0,"Fjva8n":"f1rktw8j"},"stepTools":{"mc9l5x":"f22iagw","Brf1p80":"f9c4gz4","i8kkvl":"f1ufnopg"},"mode":{"D305fb":"fjg76ag"},"reviewers":{"dclx09":"ftrb29c","Bo78w4l":0,"Cgygva":0,"yil37a":"f8ntp42","B1hrpyx":"f1jat206","mc9l5x":"f22iagw","Beiy3e4":"f1vx9l62","Belr9w4":"f778t4r"},"reviewer":{"mc9l5x":"f22iagw","Bt984gj":"f122n59","i8kkvl":"f1ufnopg","Dbcxam":0,"rjzwhg":0,"Bblux5w":"f18qryls","Bnt446c":0,"B1hrpyx":0,"Fjva8n":"f5j02bv","Beyfa6y":0,"Bbmb7ep":0,"Btl43ni":0,"B7oj6ja":0,"Dimara":"ft85np5","De3pzq":"f1dmdbja"},"reviewerName":{"Bh6795r":"fqerorx","Bgto7am":"fuzgk9b","Btd35i7":"fjksvth"},"position":{"sj55zd":"f11d4kpn","Bgto7am":"fmd5axg"},"hint":{"Bo78w4l":0,"Cgygva":0,"yil37a":"f8ntp42","sj55zd":"fkfq4zb","Be2twd7":"fy9rknc"},"adder":{"mc9l5x":"f22iagw","Eh141a":"fni485r","Bt984gj":"fgs5rwf","i8kkvl":"fsnqrgy","Belr9w4":"f1wn3y0l"},"adderField":{"Bh6795r":"fqerorx","xawz":"ffq3mkc"},"addStep":{"qb2dma":"fjgzulp"}});

export interface EditorMember {
  userId: string;
  displayName: string;
}

export interface EditorOffice {
  organizationId: string;
  name: string;
}

export interface StepsEditorProps {
  steps: readonly StepDraft[];
  onChange: (steps: StepDraft[]) => void;
  /** The organisation's people a reviewer is picked from. */
  members: readonly EditorMember[];
  /**
   * Partner offices of the organisation's shared spaces a step can also ask (COLX-BE-02): their
   * approvers in the space decide. Left out or empty, steps only name people.
   */
  offices?: readonly EditorOffice[];
  /** The people list could not be loaded. */
  membersFailed?: boolean;
  disabled?: boolean;
}

/**
 * Builds the steps of an approval: add, remove and reorder steps; each step is sequential (the
 * reviewers decide one after another, in the listed order) or parallel (any order); reviewers
 * are picked from the organisation's people (the server checks that they can open the document).
 */
export function StepsEditor({
  steps,
  onChange,
  members,
  offices = [],
  membersFailed = false,
  disabled = false,
}: StepsEditorProps) {
  const { t } = useTranslation('documents');
  const styles = useStyles();
  const [picks, setPicks] = useState<Record<string, string>>({});
  const [officePicks, setOfficePicks] = useState<Record<string, string>>({});
  const nameOf = (userId: string) =>
    members.find((member) => member.userId === userId)?.displayName ??
    t('approvals.stepper.unknownReviewer');
  const officeNameOf = (orgId: string) =>
    offices.find((office) => office.organizationId === orgId)?.name ??
    t('approvals.stepper.unknownOffice');

  const update = (
    key: string,
    change: Partial<Pick<StepDraft, 'mode' | 'reviewerIds' | 'reviewerOrgIds'>>,
  ) => {
    onChange(steps.map((step) => (step.key === key ? { ...step, ...change } : step)));
  };

  return (
    <div className={styles.root}>
      {steps.map((step, index) => {
        const n = index + 1;
        const available = members.filter((member) => !step.reviewerIds.includes(member.userId));
        const pick = picks[step.key] ?? '';
        const availableOffices = offices.filter(
          (office) => !step.reviewerOrgIds.includes(office.organizationId),
        );
        const officePick = officePicks[step.key] ?? '';
        const addOffice = () => {
          if (!officePick || step.reviewerOrgIds.length >= MAX_OFFICES) return;
          update(step.key, { reviewerOrgIds: [...step.reviewerOrgIds, officePick] });
          setOfficePicks((current) => ({ ...current, [step.key]: '' }));
        };
        const addReviewer = () => {
          if (!pick || step.reviewerIds.length >= MAX_REVIEWERS) return;
          update(step.key, { reviewerIds: [...step.reviewerIds, pick] });
          setPicks((current) => ({ ...current, [step.key]: '' }));
        };
        return (
          <fieldset key={step.key} className={styles.step} disabled={disabled}>
            <legend className={styles.legend}>{t('approvals.steps.step', { n })}</legend>
            <div className={styles.stepTools}>
              <Button
                size="small"
                appearance="subtle"
                icon={<ArrowUp16Regular />}
                aria-label={t('approvals.steps.moveStepUp', { n })}
                disabledFocusable={index === 0}
                onClick={() => {
                  onChange(moveItem(steps, index, index - 1));
                }}
              />
              <Button
                size="small"
                appearance="subtle"
                icon={<ArrowDown16Regular />}
                aria-label={t('approvals.steps.moveStepDown', { n })}
                disabledFocusable={index === steps.length - 1}
                onClick={() => {
                  onChange(moveItem(steps, index, index + 1));
                }}
              />
              <Button
                size="small"
                appearance="subtle"
                icon={<Delete16Regular />}
                aria-label={t('approvals.steps.removeStep', { n })}
                disabledFocusable={steps.length === 1}
                onClick={() => {
                  onChange(steps.filter((other) => other.key !== step.key));
                }}
              />
            </div>
            <FormField
              className={styles.mode}
              label={t('approvals.steps.mode', { n })}
              hint={t(`approvals.steps.${step.mode}Hint`)}
            >
              <Select
                value={step.mode}
                onChange={(_event, data) => {
                  const mode = STEP_MODES.find((candidate) => candidate === data.value);
                  if (mode) update(step.key, { mode });
                }}
              >
                {STEP_MODES.map((mode) => (
                  <option key={mode} value={mode}>
                    {t(`approvals.steps.${mode}`)}
                  </option>
                ))}
              </Select>
            </FormField>

            {step.reviewerIds.length > 0 ? (
              <ol className={styles.reviewers} aria-label={t('approvals.steps.reviewersOf', { n })}>
                {step.reviewerIds.map((userId, position) => {
                  const name = isolate(nameOf(userId));
                  return (
                    <li key={userId} className={styles.reviewer}>
                      {step.mode === 'sequential' ? (
                        <span className={styles.position} aria-hidden="true">
                          {position + 1}.
                        </span>
                      ) : null}
                      <bdi className={styles.reviewerName}>{nameOf(userId)}</bdi>
                      {step.mode === 'sequential' ? (
                        <>
                          <Button
                            size="small"
                            appearance="subtle"
                            icon={<ArrowUp16Regular />}
                            aria-label={t('approvals.steps.moveReviewerUp', { name, n })}
                            disabledFocusable={position === 0}
                            onClick={() => {
                              update(step.key, {
                                reviewerIds: moveItem(step.reviewerIds, position, position - 1),
                              });
                            }}
                          />
                          <Button
                            size="small"
                            appearance="subtle"
                            icon={<ArrowDown16Regular />}
                            aria-label={t('approvals.steps.moveReviewerDown', { name, n })}
                            disabledFocusable={position === step.reviewerIds.length - 1}
                            onClick={() => {
                              update(step.key, {
                                reviewerIds: moveItem(step.reviewerIds, position, position + 1),
                              });
                            }}
                          />
                        </>
                      ) : null}
                      <Button
                        size="small"
                        appearance="subtle"
                        icon={<Delete16Regular />}
                        aria-label={t('approvals.steps.removeReviewer', { name, n })}
                        onClick={() => {
                          update(step.key, {
                            reviewerIds: step.reviewerIds.filter((id) => id !== userId),
                          });
                        }}
                      />
                    </li>
                  );
                })}
              </ol>
            ) : step.reviewerOrgIds.length === 0 ? (
              <p className={styles.hint}>{t('approvals.steps.noReviewers')}</p>
            ) : null}

            <div className={styles.adder}>
              {available.length > 0 ? (
                <>
                  <FormField
                    className={styles.adderField}
                    label={t('approvals.steps.addReviewerLabel', { n })}
                  >
                    <Select
                      value={pick}
                      onChange={(_event, data) => {
                        setPicks((current) => ({ ...current, [step.key]: data.value }));
                      }}
                    >
                      <option value="">{t('approvals.steps.addPlaceholder')}</option>
                      {available.map((member) => (
                        <option key={member.userId} value={member.userId}>
                          {member.displayName}
                        </option>
                      ))}
                    </Select>
                  </FormField>
                  <Button
                    appearance="secondary"
                    icon={<PersonAdd20Regular />}
                    aria-label={t('approvals.steps.addReviewerTo', { n })}
                    disabledFocusable={!pick || step.reviewerIds.length >= MAX_REVIEWERS}
                    onClick={addReviewer}
                  >
                    {t('approvals.steps.addReviewer')}
                  </Button>
                </>
              ) : membersFailed ? (
                <p className={styles.hint}>{t('approvals.steps.membersFailed')}</p>
              ) : (
                <p className={styles.hint}>{t('approvals.steps.noneToAdd')}</p>
              )}
            </div>

            {offices.length > 0 ? (
              <>
                {step.reviewerOrgIds.length > 0 ? (
                  <>
                    <ul
                      className={styles.reviewers}
                      aria-label={t('approvals.steps.officesOf', { n })}
                    >
                      {step.reviewerOrgIds.map((orgId) => {
                        const name = isolate(officeNameOf(orgId));
                        return (
                          <li key={orgId} className={styles.reviewer}>
                            <Building20Regular aria-hidden="true" />
                            <bdi className={styles.reviewerName}>{officeNameOf(orgId)}</bdi>
                            <Button
                              size="small"
                              appearance="subtle"
                              icon={<Delete16Regular />}
                              aria-label={t('approvals.steps.removeOffice', { name, n })}
                              onClick={() => {
                                update(step.key, {
                                  reviewerOrgIds: step.reviewerOrgIds.filter((id) => id !== orgId),
                                });
                              }}
                            />
                          </li>
                        );
                      })}
                    </ul>
                    <p className={styles.hint}>{t('approvals.steps.officeHint')}</p>
                  </>
                ) : null}
                {availableOffices.length > 0 ? (
                  <div className={styles.adder}>
                    <FormField
                      className={styles.adderField}
                      label={t('approvals.steps.addOfficeLabel', { n })}
                    >
                      <Select
                        value={officePick}
                        onChange={(_event, data) => {
                          setOfficePicks((current) => ({ ...current, [step.key]: data.value }));
                        }}
                      >
                        <option value="">{t('approvals.steps.addOfficePlaceholder')}</option>
                        {availableOffices.map((office) => (
                          <option key={office.organizationId} value={office.organizationId}>
                            {office.name}
                          </option>
                        ))}
                      </Select>
                    </FormField>
                    <Button
                      appearance="secondary"
                      icon={<Building20Regular />}
                      aria-label={t('approvals.steps.addOfficeTo', { n })}
                      disabledFocusable={!officePick || step.reviewerOrgIds.length >= MAX_OFFICES}
                      onClick={addOffice}
                    >
                      {t('approvals.steps.addOffice')}
                    </Button>
                  </div>
                ) : null}
              </>
            ) : null}
          </fieldset>
        );
      })}
      <Button
        className={styles.addStep}
        appearance="secondary"
        icon={<Add20Regular />}
        disabledFocusable={disabled || steps.length >= MAX_STEPS}
        onClick={() => {
          onChange([...steps, newStep()]);
        }}
      >
        {t('approvals.steps.addStep')}
      </Button>
    </div>
  );
}


import "/opt/batiflow/app/apps/web/src/features/documents/approvals/steps-editor.griffel.css";